import {
  clearCacheFromAPI,
  getLocalStorage,
  getProductPageType,
  isTextureProductPage,
} from '../helpers/jsHelper';
import { WS_BASE_URL } from '../constants/projectConstants';
import { notificationInfoToast, successToast } from '../helpers/toastHelper';
import { useEffect } from 'react';
import { NOTIFICATION_PLACEHOLDER_LABEL } from '../common/notifications/NotificationsPanelCard';
import { isCuratorPage } from '../helpers/urlHelper';
import { useUserContext } from '../contexts/UserContext';
import { apiGetNewNotifications, apiGetPreviewAndMainRender } from '../helpers/api';
import { v4 as uuidv4 } from 'uuid';
import { myprojectsSelectors } from '../redux/slicers/my-projects/myProjectsSlicer';
import { getAppState } from '../redux/store';
import { updateSubscriptionsData } from './useSubscriptionSetupAPICalls';
import { updateConfiguratorDataRealTime } from '../components/Configurator/utils';
import { useLoadNotReadyProduct } from './useLoadNotReadyProduct';
import { selectProductNotReady } from '../redux/slicers/admin/curatorSlicer';
import { userPilotEvents } from '../common/Userpilot/userpilotHelper';
import useCurrentOrganizationInfo from './useCurrentOrganizationInfo';
import { dispatcher } from '../helpers/projectHelper';
import {
  setTriggerProductContentUpdate,
  setTriggerProductUpdate,
} from '../redux/slicers/productV2Slicer';
import { updatePublicLinkProductTexture } from '../screens/NewProductUIV2/ShareButtonV2/helpers';
import { setRefreshProductAPI } from '../redux/slicers/activitySlicer';
import {
  isMattressBuilderPage,
  isRetailAutomationPage,
} from '../components/admin/curator/ProductBuilderComponents/commonHelper';
import { sendDataToFrappeApp } from "../screens/Requests/RequestsV2";

const NotificationConfig = {
  position: 'bottom-right',
  autoClose: 10000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  pauseOnFocusLoss: false,
  draggable: true,
  progress: 0,
  limit: 1,
};

export const useRealNotifications = () => {
  const { currentUser, token, updateUserData } = useUserContext();

  const { fetchAndLoadPropInUnity } = useLoadNotReadyProduct();

  const { trackRenderSuccessful } = userPilotEvents();

  const { isFreemiumOrSelfServeUser } = useCurrentOrganizationInfo();

  useEffect(() => {
    if (
      !currentUser?.organization ||
      isCuratorPage() ||
      isRetailAutomationPage() ||
      isMattressBuilderPage()
    )
      return;
    apiGetNewNotifications();
  }, [currentUser?.organization]);

  useEffect(() => {
    if (!token) return;

    const LF_WEBSOCKET_LOGS_ENABLED =
      JSON.parse(getLocalStorage('LF_WEBSOCKET_LOGS_ENABLED')) || null;

    const socketURL = `${WS_BASE_URL}/ws/notification/${token}/`;
    const socket = new WebSocket(socketURL);

    socket.onopen = function (e) {
      if (LF_WEBSOCKET_LOGS_ENABLED) {
        console.log('WebSocket: Connection established');
      }
    };

    socket.onmessage = function (event) {
      if (LF_WEBSOCKET_LOGS_ENABLED) {
        console.log(`WebSocket: data received from server: ${event.data}`);
      }

      const results = event.data ? JSON.parse(event.data) : undefined;

      let msg = undefined;
      const notificationDetails = results?.results;
      if (notificationDetails && Object.keys(notificationDetails).length > 2) {
        const { verb, name, type, level, placeholder_data, ...rest } = notificationDetails;

        //  Setting setRefreshProductAPI to true which triggers the product api call to get the latest data
        if (['product'].includes(type)) {
          dispatcher(setRefreshProductAPI(true));
        }

        // updating timestamp when we get the notification about the product update in the retail automation
        if (
          rest?.model_type?.toLowerCase() === 'product' &&
          ['update', 'create', 'delete'].includes(rest?.event_type?.toLowerCase())
        ) {
          clearCacheFromAPI();
        }

        if (!verb) return;

        if (placeholder_data) {
          msg = verb;
          Object.entries(placeholder_data).forEach(([key, value]) => {
            msg = msg.replace(`{${key}}`, value);
          });
        } else if (verb.includes(NOTIFICATION_PLACEHOLDER_LABEL)) {
          msg = verb.replace(NOTIFICATION_PLACEHOLDER_LABEL, `"${name}"`);
        } else {
          msg = verb;
        }

        if (msg) {
          const toastId = uuidv4();
          // type: "normal" -> means notifications for project upload
          if ((isCuratorPage() || ['normal', 'request_v2'].includes(type)) && !document.hidden)
            notificationInfoToast(level, msg, NotificationConfig, toastId);
          if (
            type !== 'normal' &&
            !isCuratorPage() &&
            !isRetailAutomationPage() &&
            !isMattressBuilderPage()
          )
            apiGetNewNotifications();

          const isProjectUploaded = myprojectsSelectors.selectIsItemUploading(getAppState());
          if (
            type === 'normal' &&
            window.location.pathname.includes('projects') &&
            !isProjectUploaded
          ) {
            setTimeout(function () {
              window.location.reload();
            }, 5000);
          }

          if (type === 'system_command' && 'is_editor_access' in notificationDetails) {
            updateSubscriptionsData(
              isFreemiumOrSelfServeUser ? { is_new_self_serve_plan: true } : {}
            );
            updateUserData();
          }

          if (
            'plan_id' in notificationDetails &&
            !verb?.toLowerCase()?.includes('downgraded') &&
            verb?.toLowerCase()?.includes('subscription')
          ) {
            updateSubscriptionsData(
              isFreemiumOrSelfServeUser ? { is_new_self_serve_plan: true } : {}
            );
          }

          if (type === 'configurator' && 'configurator_data' in notificationDetails) {
            updateConfiguratorDataRealTime({ data: notificationDetails?.configurator_data });
          }

          /**
           * Handles the case where the notification type is 'product' and the notification details contain a 'target_object_id' property.
           * If there are products that are not ready in the application state, it fetches and loads the product data with the given 'target_object_id' into Unity.
           */
          if (type === 'product' && 'target_object_id' in notificationDetails) {
            if (selectProductNotReady(getAppState())) {
              fetchAndLoadPropInUnity(rest?.target_object_id);
            }
          }

          /**
           * If the notification type is 'preview' or 'render_status' and the level is 'success',
           * it fetches the preview or render data for the given 'target_object_id' and
           * sends a user pilot event with the render name and type.
           */
          if (
            ['preview', 'render_status'].includes(type) &&
            level === 'success' &&
            'target_object_id' in notificationDetails
          ) {
            const params = notificationDetails?.render_data?.is_automation
              ? '?is_automation=true'
              : '?is_automation=false';
            apiGetPreviewAndMainRender(rest?.target_object_id, null, null, params).then(
              (response) => {
                trackRenderSuccessful({
                  is_automation_render: notificationDetails?.render_data?.is_automation
                    ? 'Yes'
                    : 'No',
                  render_name: response?.name,
                  render_type: response?.is_preview ? 'Preview' : 'Render',
                });
              }
            );
          }

          // setting setTriggerProductContentUpdate to true which triggers the product/texture category content api call to get the latest data
          if (
            [
              'video',
              'panningvideo',
              'aivideo',
              'product360viewer',
              'preview',
              'render_status',
              'product',
            ].includes(type) &&
            'target_object_id' in notificationDetails
          ) {
            // here, in case of render_status & preview the target_object_id is the render id & in other case this is the product/texture id
            const value = ['render_status', 'preview'].includes(type)
              ? notificationDetails?.render_data?.prop || notificationDetails?.render_data?.texture // true
              : notificationDetails?.target_object_id;

            const typeMapWithCategory = {
              video: getProductPageType().VIDEO,
              panningvideo: getProductPageType().VIDEO,
              aivideo: getProductPageType().VIDEO,
              product360viewer: getProductPageType().THREESIXTY,
              preview: getProductPageType().PRODUCTIMAGE,
              render_status: getProductPageType().PRODUCTIMAGE,
              product: getProductPageType().THREEDVIEWER,
            };

            dispatcher(
              setTriggerProductContentUpdate({ id: value, category: typeMapWithCategory?.[type] })
            );
            updatePublicLinkProductTexture({ isTexture: isTextureProductPage() });
          }

          if (['glbfile'].includes(type) && 'target_object_id' in notificationDetails) {
            dispatcher(setTriggerProductUpdate(notificationDetails?.target_object_id));
          }

          if('request_v2' === type) {
            sendDataToFrappeApp({ refreshRequestList: true });
          }
        }
      }
    };

    socket.onclose = function (event) {
      if (event.wasClean) {
        if (LF_WEBSOCKET_LOGS_ENABLED) {
          console.log(
            `WebSocket: Connection closed cleanly, code=${event.code} reason=${event.reason}`
          );
        }
      } else {
        // e.g. server process killed or network down
        // event.code is usually 1006 in this case

        if (LF_WEBSOCKET_LOGS_ENABLED) {
          console.log('WebSocket: Connection died');
        }
      }
    };

    socket.onerror = function (error) {
      if (LF_WEBSOCKET_LOGS_ENABLED) {
        console.log(`WebSocket: [error] ${error.message}`);
      }
    };

    return () => socket.close();
  }, [token]);

  return;
};
